import type { AiqTriangleThresholds } from '../../types/aiq-report'

export interface TriangleScorePoint {
  label: string
  value: number   // 0-200 (standard: 0-100, advanced: 0-200)
  color: string
  /** Optional second point for comparison (e.g., Pre vs Post) */
  comparisonValue?: number
  comparisonColor?: string
}

export interface TriangleChartProps {
  scores: TriangleScorePoint[]
  thresholds: AiqTriangleThresholds
  title?: string
  maxScore?: number  // 100 for standard, 200 for advanced
  height?: number
  width?: number
  showArrows?: boolean  // comparison mode
}

/**
 * Custom SVG 삼각형 차트
 *
 * 삼각형의 세로 중앙선 위에 점을 찍어 점수를 시각화합니다.
 * - 100(또는 maxScore)은 꼭짓점, 0은 밑변 중앙점
 * - firstThird, secondThird로 3구간 분할
 * - 여러 점을 좌/중/우 오프셋으로 배치
 */
export function TriangleChart({
  scores,
  thresholds,
  title,
  maxScore = 100,
  height = 260,
  width = 200,
  showArrows = false,
}: TriangleChartProps) {
  const { firstThird, secondThird } = thresholds

  // Triangle geometry
  const padding = { top: 24, bottom: 16, left: 16, right: 16 }
  const triTop = { x: width / 2, y: padding.top }
  const triBottomLeft = { x: padding.left + 10, y: height - padding.bottom }
  const triBottomRight = { x: width - padding.right - 10, y: height - padding.bottom }
  const triBottomCenter = { x: width / 2, y: height - padding.bottom }

  const triHeight = triBottomCenter.y - triTop.y

  // Score to Y position (0 → bottom, maxScore → top)
  const scoreToY = (score: number) => {
    const ratio = Math.min(score, maxScore) / maxScore
    return triBottomCenter.y - ratio * triHeight
  }

  // Width of triangle at a given Y position
  const widthAtY = (y: number) => {
    const ratio = (triBottomCenter.y - y) / triHeight
    const baseWidth = triBottomRight.x - triBottomLeft.x
    return baseWidth * (1 - ratio)
  }

  // Zone divider Y positions
  const firstThirdY = scoreToY(firstThird)
  const secondThirdY = scoreToY(secondThird)

  // X offset for multiple dots (-1=left, 0=center, 1=right)
  const getXOffset = (index: number, total: number) => {
    if (total === 1) return 0
    if (total === 2) return index === 0 ? -1 : 1
    if (total === 3) return index - 1 // -1, 0, 1
    return 0
  }

  // Zone label positions
  const zoneLabelX = width - padding.right - 4
  const zoneLabels = [
    { y: (triTop.y + firstThirdY) / 2, text: `${firstThird}~${maxScore}` },
    { y: (firstThirdY + secondThirdY) / 2, text: `${secondThird}~${firstThird}` },
    { y: (secondThirdY + triBottomCenter.y) / 2, text: `0~${secondThird}` },
  ]

  // Zone divider line endpoints (clipped to triangle)
  const getDividerEndpoints = (y: number) => {
    const w = widthAtY(y)
    const cx = width / 2
    return { x1: cx - w / 2, x2: cx + w / 2, y }
  }

  const divider1 = getDividerEndpoints(firstThirdY)
  const divider2 = getDividerEndpoints(secondThirdY)

  return (
    <div className="flex flex-col items-center">
      {title && (
        <h4 className="text-sm font-semibold text-gray-700 mb-2">{title}</h4>
      )}
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
        {/* Triangle fill */}
        <polygon
          points={`${triTop.x},${triTop.y} ${triBottomLeft.x},${triBottomLeft.y} ${triBottomRight.x},${triBottomRight.y}`}
          fill="#f8f9ff"
          stroke="#c7d2fe"
          strokeWidth={1.5}
        />

        {/* Zone dividers */}
        <line
          x1={divider1.x1} y1={divider1.y}
          x2={divider1.x2} y2={divider1.y}
          stroke="#c7d2fe" strokeWidth={1} strokeDasharray="4,3"
        />
        <line
          x1={divider2.x1} y1={divider2.y}
          x2={divider2.x2} y2={divider2.y}
          stroke="#c7d2fe" strokeWidth={1} strokeDasharray="4,3"
        />

        {/* Zone labels */}
        {zoneLabels.map((zl, i) => (
          <text
            key={i}
            x={zoneLabelX}
            y={zl.y}
            fontSize={9}
            fill="#94a3b8"
            textAnchor="end"
            dominantBaseline="middle"
          >
            {zl.text}
          </text>
        ))}

        {/* Score dots */}
        {scores.map((score, i) => {
          const y = scoreToY(score.value)
          const xOffset = getXOffset(i, scores.length) * 14
          const x = width / 2 + xOffset

          const compY = score.comparisonValue != null ? scoreToY(score.comparisonValue) : null
          const compX = x

          return (
            <g key={i}>
              {/* Main dot */}
              <circle cx={x} cy={y} r={6} fill={score.color} opacity={0.9} />
              <text
                x={x}
                y={y - 10}
                fontSize={10}
                fill={score.color}
                textAnchor="middle"
                fontWeight="bold"
              >
                {Math.round(score.value)}
              </text>

              {/* Comparison dot + arrow */}
              {compY != null && score.comparisonColor && (
                <>
                  <circle cx={compX} cy={compY} r={6} fill={score.comparisonColor} opacity={0.9} />
                  <text
                    x={compX}
                    y={compY - 10}
                    fontSize={10}
                    fill={score.comparisonColor}
                    textAnchor="middle"
                    fontWeight="bold"
                  >
                    {Math.round(score.comparisonValue!)}
                  </text>

                  {/* Arrow from main to comparison */}
                  {showArrows && (
                    <line
                      x1={x} y1={y}
                      x2={compX} y2={compY}
                      stroke={score.comparisonValue! > score.value ? '#22c55e' : '#ef4444'}
                      strokeWidth={1.5}
                      markerEnd="url(#arrowhead)"
                      opacity={0.7}
                    />
                  )}
                </>
              )}

              {/* Label below triangle */}
              <text
                x={x}
                y={height - 2}
                fontSize={10}
                fill="#6b7280"
                textAnchor="middle"
              >
                {score.label}
              </text>
            </g>
          )
        })}

        {/* Arrow marker definition */}
        <defs>
          <marker
            id="arrowhead"
            markerWidth="8"
            markerHeight="6"
            refX="8"
            refY="3"
            orient="auto"
          >
            <polygon points="0 0, 8 3, 0 6" fill="#6b7280" />
          </marker>
        </defs>
      </svg>
    </div>
  )
}
